import { useState } from "react";
import type { ListingTemplateProduct } from "@/lib/listing-templates";
import { Stars, TemplatePoweredFooter } from "./shared";
import { imageAt, brandOf, trustSections } from "./template-helpers";

/**
 * Template 3 — Marketplace Classic.
 * eBay-style long-form listing: strong title/header, image presentation,
 * description, specification table, and shipping/returns/support sections.
 */
export default function MarketplaceClassicTemplate({ product }: { product: ListingTemplateProduct }) {
  const [activeImage, setActiveImage] = useState(0);
  const hero = imageAt(product, activeImage) ?? imageAt(product, 0);
  const brand = brandOf(product);
  const sections = trustSections(product);

  return (
    <div className="bg-white text-slate-800 font-sans">
      {/* Title header */}
      <div className="border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 py-5">
          <p className="text-xs text-slate-500 flex items-center gap-2 mb-1.5 flex-wrap">
            {brand && <span className="text-amber-600 font-medium">{brand}</span>}
            {brand && <span className="text-slate-300">·</span>}
            <span>{product.condition}</span>
          </p>
          <h1 className="text-2xl font-semibold leading-snug break-words">{product.title}</h1>
          <div className="flex items-center gap-4 mt-2 flex-wrap">
            <Stars score={product.rating.score} count={product.rating.count} />
            <span className="text-xl font-bold">{product.price}</span>
            {product.wasPrice && <span className="text-sm text-slate-400 line-through">{product.wasPrice}</span>}
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-8">
        {/* Gallery */}
        {hero && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="md:col-span-4 aspect-[4/3] rounded-lg overflow-hidden bg-slate-100 border border-slate-200">
              <img src={hero.src} alt={hero.alt} data-testid="template-hero-image" className="w-full h-full object-contain object-center" />
            </div>
            {product.images.length > 1 && (
              <div className="flex md:flex-col gap-2 overflow-x-auto">
                {product.images.slice(0, 5).map((img, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setActiveImage(i)}
                    className={`shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 ${
                      activeImage === i ? "border-amber-500" : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-top" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* About this item */}
        {product.keyFeatures.length > 0 && (
          <section>
            <h2 className="text-lg font-semibold border-b border-slate-200 pb-2 mb-4">About this item</h2>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              {product.keyFeatures.map((feature) => (
                <li key={feature} className="flex items-start gap-2">
                  <span className="text-amber-600">•</span>
                  <span className="break-words">{feature}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Description */}
        {product.fullDescription.length > 0 && (
          <section>
            <h2 className="text-lg font-semibold border-b border-slate-200 pb-2 mb-4">Description</h2>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              {product.fullDescription.map((para) => (
                <p key={para.slice(0, 24)}>{para}</p>
              ))}
            </div>
          </section>
        )}

        {/* Item specifications */}
        {product.specifications.length > 0 && (
          <section>
            <h2 className="text-lg font-semibold border-b border-slate-200 pb-2 mb-4">Item specifications</h2>
            <div className="overflow-hidden rounded-lg border border-slate-200">
              <table className="w-full text-sm">
                <tbody>
                  {product.specifications.map((spec, i) => (
                    <tr key={spec.label} className={i % 2 === 0 ? "bg-slate-50" : "bg-white"}>
                      <td className="px-3 py-2.5 font-medium text-slate-500 w-2/5 sm:w-1/3 align-top break-words leading-snug">{spec.label}</td>
                      <td className="px-3 py-2.5 text-slate-800 align-top break-words leading-snug">{spec.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Shipping / returns / support */}
        {sections.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {sections.map((section) => (
              <section key={section.title} className="rounded-lg border border-slate-200 p-5">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <i className="ri-truck-line text-amber-600" /> {section.title}
                </h3>
                <ul className="space-y-2 text-sm text-slate-600">
                  {section.lines.map((line) => (
                    <li key={line} className="leading-relaxed break-words">
                      {line}
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </main>

      <footer className="bg-slate-50 border-t border-slate-200">
        <div className="max-w-5xl mx-auto px-4 py-5">
          <TemplatePoweredFooter
            testId="marketplace-classic-footer"
            left={`${brand ?? "Store"} · eBay item`}
            sku={product.sku}
            className="text-slate-500"
            centerClassName="text-center text-slate-400"
          />
        </div>
      </footer>
    </div>
  );
}